/**
 * Cómo se LEE un patrón de selección del contrato (`PatronSeleccion`) en el registro.
 *
 * La tabla CA × semana que emitía estos patrones se quitó de la consola el 2026-09-10 (BB-2000: los
 * filtros de arriba son el único filtro, y el resumen solo enseña la distribución). El front ya no
 * manda `seleccion` en ningún preview. Pero el audit log es permanente (invariante 6) y las
 * confirmaciones anteriores a ese día llevan `criterios.seleccion` — y el registro tiene que seguir
 * explicando qué se seleccionó en ellas (invariante 4). Este módulo existe solo para eso.
 *
 * Dice explícitamente "todas las fechas" / "todas las comunidades" en los patrones de fila y de
 * columna, en vez de callarse la mitad ausente: un chip que dijera solo "Galicia" no distingue entre
 * haber clicado esa comunidad entera y haber clicado una de sus celdas, y son cantidades de dinero
 * muy distintas. Y el periodo se escribe tal como se filtró: un mes dice el mes ("abril de 2026"),
 * no el rango de semanas que lo tapaba.
 */
import type { PatronSeleccion } from '@caes-payments/shared';

import { deFechaLocal } from './formato';

/** Etiqueta del bucket sin CA: `sin CA` explícito, nunca un hueco que parezca un fallo. */
export const ETIQUETA_SIN_CA = 'sin CA';

const MES_ANIO = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' });
const DIA_MES = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short' });
const DIA = new Intl.DateTimeFormat('es-ES', { day: 'numeric' });

/**
 * `'2026-05-04'` -> `'4-10 may'`. El RANGO y no un número de semana: "semana 19" no le dice a nadie
 * qué días son.
 *
 * Aritmética en local vía `deFechaLocal`, nunca `toISOString()`: el desplazamiento a UTC movería el
 * lunes al domingo anterior para cualquiera al este de Greenwich, y la etiqueta mentiría sobre qué
 * semana es (el mismo error que `formato.ts` documenta para el filtro de fechas).
 */
export function etiquetaSemana(semanaInicio: string): string {
  const inicio = deFechaLocal(semanaInicio);
  const fin = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + 6);

  // Mismo mes: el mes se dice una vez ("4-10 may"). Meses distintos, las dos veces ("27 abr-3 may").
  const mismoMes = inicio.getMonth() === fin.getMonth();
  const izquierda = mismoMes ? DIA.format(inicio) : DIA_MES.format(inicio);
  return `${izquierda}-${DIA_MES.format(fin)}`;
}

/** `'2026-04-01'` -> `'abril de 2026'`. */
export function etiquetaMes(mesInicio: string): string {
  return MES_ANIO.format(deFechaLocal(mesInicio));
}

export function describirPatron(patron: PatronSeleccion): string {
  const ca =
    patron.regionName === undefined
      ? 'todas las comunidades'
      : (patron.regionName ?? ETIQUETA_SIN_CA);
  const periodo =
    patron.mesInicio !== undefined
      ? etiquetaMes(patron.mesInicio)
      : patron.semanaInicio !== undefined
        ? etiquetaSemana(patron.semanaInicio)
        : 'todas las fechas';
  return `${ca} · ${periodo}`;
}
